import { useEffect, useState } from "react";
import { api, formatCedi } from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { Loader2, Send, Star, TrendingUp, Users, Target, DollarSign } from "lucide-react";
import BeforeAfter from "../components/common/BeforeAfter";
import ConsultationCTA from "../components/common/ConsultationCTA";
import CountryPhone from "../components/common/CountryPhone";
import { buildFullPhone, DEFAULT_COUNTRY } from "../lib/countries";

const STATUSES = ["New", "Contacted", "Qualified", "Proposal Sent", "Closed"];

const AUTOMATION_AREAS = [
  { id: "customer service", label: "Customer service" },
  { id: "lead capture", label: "Lead capture / CRM" },
  { id: "bookings", label: "Bookings" },
  { id: "invoicing", label: "Invoicing" },
  { id: "inventory", label: "Inventory" },
  { id: "hr", label: "HR & onboarding" },
  { id: "analytics", label: "Analytics" },
];

const initial = {
  name: "", email: "", phone: "", country: DEFAULT_COUNTRY, company: "", interest: "", budget: "", message: "",
  industry: "", employees: "", automation_areas: [],
};

const DemoHeader = () => (
  <div className="mb-8">
    <div className="text-xs font-semibold uppercase tracking-widest text-primary">Demo 2</div>
    <h1 className="mt-1 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight">Lead Capture & CRM Automation</h1>
    <p className="mt-3 text-muted-foreground text-lg max-w-3xl">
      Every enquiry lands in one place. Watch a lead flow from form to Kanban to closed-won — automatically qualified and scored by AI.
    </p>
  </div>
);

const KPI = ({ icon: Icon, label, value, hint }) => (
  <Card className="glass"><CardContent className="p-5">
    <div className="flex items-center justify-between">
      <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary grid place-items-center"><Icon className="w-4 h-4"/></div>
    </div>
    <div className="mt-2 font-display text-2xl font-bold">{value}</div>
    {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
  </CardContent></Card>
);

const parseBudget = (b) => {
  const digits = (b || "").replace(/[^0-9]/g, "");
  return digits ? parseInt(digits, 10) : 0;
};

const LeadForm = ({ onCreated }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleArea = (id) =>
    setForm((f) => ({
      ...f,
      automation_areas: f.automation_areas.includes(id)
        ? f.automation_areas.filter((a) => a !== id)
        : [...f.automation_areas, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) return toast.error("Name, email and phone are required.");
    setLoading(true);
    const payload = {
      ...form,
      phone: buildFullPhone(form.country, form.phone),
      employees: form.employees ? Number(form.employees) : null,
      automation_areas: form.automation_areas,
    };
    try {
      const { data } = await api.post("/leads", payload);
      toast.success(`Lead qualified — score ${data.score}/100`);
      setForm(initial);
      onCreated?.();
    } catch { toast.error("Could not save. Try again."); }
    finally { setLoading(false); }
  };

  return (
    <Card className="glass"><CardContent className="p-6">
      <div className="mb-4">
        <div className="text-xs uppercase tracking-widest text-primary">Public contact form</div>
        <div className="font-display text-lg font-semibold">Submit a lead — the AI qualifies it instantly</div>
      </div>
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={update("name")} data-testid="lead-name"/></div>
          <div><Label>Company</Label><Input value={form.company} onChange={update("company")} data-testid="lead-company"/></div>
          <div><Label>Email *</Label><Input type="email" value={form.email} onChange={update("email")} data-testid="lead-email"/></div>
          <div className="sm:col-span-2">
            <Label>Phone *</Label>
            <CountryPhone
              testIdPrefix="lead-phone"
              country={form.country}
              phone={form.phone}
              onChange={({ country, phone }) => setForm((f) => ({ ...f, country, phone }))}
            />
          </div>
          <div><Label>Industry</Label><Input placeholder="e.g. Retail, Restaurant, Manufacturing" value={form.industry} onChange={update("industry")} data-testid="lead-industry"/></div>
          <div><Label>Employees</Label><Input type="number" min={0} placeholder="e.g. 20" value={form.employees} onChange={update("employees")} data-testid="lead-employees"/></div>
          <div><Label>Interest</Label><Input placeholder="e.g. CRM automation" value={form.interest} onChange={update("interest")} data-testid="lead-interest"/></div>
          <div><Label>Budget (GHS)</Label><Input placeholder="e.g. 5,000" value={form.budget} onChange={update("budget")} data-testid="lead-budget"/></div>
        </div>
        <div>
          <Label>Areas to automate</Label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="lead-areas">
            {AUTOMATION_AREAS.map((a) => {
              const on = form.automation_areas.includes(a.id);
              return (
                <button
                  type="button"
                  key={a.id}
                  onClick={() => toggleArea(a.id)}
                  data-testid={`area-${a.id.replace(/\s+/g, "-")}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors duration-200 ${on
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border hover:bg-muted"}`}
                >
                  {a.label}
                </button>
              );
            })}
          </div>
        </div>
        <div><Label>Message</Label><Textarea rows={3} value={form.message} onChange={update("message")} data-testid="lead-message"/></div>
        <Button type="submit" disabled={loading} className="w-full h-11" data-testid="lead-submit">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Send className="w-4 h-4 mr-2"/>} Submit lead
        </Button>
      </form>
    </CardContent></Card>
  );
};

const LeadCard = ({ lead, onDragStart }) => (
  <div
    draggable
    onDragStart={(e) => { e.dataTransfer.setData("text/plain", lead.id); onDragStart?.(lead); }}
    className="kanban-card cursor-grab active:cursor-grabbing rounded-xl border border-border bg-background p-3 shadow-sm hover:shadow-md transition-transform"
    data-testid={`lead-card-${lead.id}`}
  >
    <div className="flex items-center justify-between">
      <div className="font-medium text-sm truncate">{lead.name}</div>
      <Badge variant={lead.score >= 75 ? "default" : "secondary"} className="text-[10px]">
        <Star className="w-3 h-3 mr-0.5"/> {lead.score}
      </Badge>
    </div>
    <div className="text-xs text-muted-foreground truncate">{lead.company || lead.email}</div>
    <div className="mt-1 flex flex-wrap gap-1">
      {lead.industry && <Badge variant="outline" className="text-[10px]">{lead.industry}</Badge>}
      {lead.employees ? <Badge variant="outline" className="text-[10px]">{lead.employees} staff</Badge> : null}
    </div>
    {lead.automation_areas && lead.automation_areas.length > 0 && (
      <div className="mt-1 flex flex-wrap gap-1">
        {lead.automation_areas.slice(0, 3).map((a) => (
          <Badge key={a} variant="secondary" className="text-[10px] capitalize">{a}</Badge>
        ))}
        {lead.automation_areas.length > 3 && (
          <Badge variant="secondary" className="text-[10px]">+{lead.automation_areas.length - 3}</Badge>
        )}
      </div>
    )}
    {lead.interest && <div className="mt-2 text-[11px]"><Badge variant="outline">{lead.interest}</Badge></div>}
    {lead.budget && <div className="mt-2 text-xs font-semibold text-primary">{formatCedi(parseBudget(lead.budget))}</div>}
    <div className="mt-2"><Progress value={lead.score}/></div>
  </div>
);

export default function Demo2CRM() {
  const [leads, setLeads] = useState([]);

  const load = async () => {
    const { data } = await api.get("/leads");
    setLeads(data);
  };
  useEffect(() => { load(); }, []);

  const moveLead = async (id, status) => {
    setLeads((L) => L.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await api.patch(`/leads/${id}`, { status }); }
    catch { toast.error("Could not update status"); load(); }
  };

  const grouped = STATUSES.map((s) => ({ status: s, items: leads.filter((l) => l.status === s) }));
  const totalValue = leads.reduce((sum, l) => sum + parseBudget(l.budget), 0);
  const qualified = leads.filter((l) => ["Qualified", "Proposal Sent", "Closed"].includes(l.status)).length;
  const avgScore = leads.length ? Math.round(leads.reduce((s, l) => s + l.score, 0) / leads.length) : 0;

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
      <DemoHeader/>
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-4 mb-6">
        <KPI icon={Users} label="Total leads" value={leads.length} hint="All time"/>
        <KPI icon={Target} label="Qualified+" value={qualified} hint="Qualified through Closed"/>
        <KPI icon={TrendingUp} label="Avg lead score" value={`${avgScore}/100`} hint="AI qualification"/>
        <KPI icon={DollarSign} label="Pipeline value" value={formatCedi(totalValue)} hint="Sum of budgets"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1"><LeadForm onCreated={load}/></div>
        <div className="lg:col-span-2">
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="kanban-board">
            {grouped.map((col) => (
              <div
                key={col.status}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); const id = e.dataTransfer.getData("text/plain"); moveLead(id, col.status); }}
                className="rounded-2xl bg-muted/50 p-3 min-h-[380px]"
                data-testid={`kanban-col-${col.status.replace(/\s+/g, "-").toLowerCase()}`}
              >
                <div className="flex items-center justify-between mb-3 px-1">
                  <div className="text-xs font-semibold uppercase tracking-widest">{col.status}</div>
                  <Badge variant="secondary" className="text-[10px]">{col.items.length}</Badge>
                </div>
                <div className="space-y-2">
                  {col.items.map((l) => <LeadCard key={l.id} lead={l}/>)}
                  {col.items.length === 0 && (
                    <div className="text-[11px] text-muted-foreground text-center py-6 border border-dashed border-border/60 rounded-lg">
                      Drag a lead here
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-muted-foreground">Tip: drag any lead card between columns to update its status.</p>
        </div>
      </div>

      <BeforeAfter demoId={2}/>
      <ConsultationCTA demoLabel="Lead Capture & CRM"/>
    </div>
  );
}
